import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import { languageLocked } from '../i18n';
import { budgetGuardApi, PlatformSetting } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || '';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const [activeSection, setActiveSection] = useState('account');

  // Profile form
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 2FA state
  const [tfaStep, setTfaStep] = useState<'idle' | 'setup' | 'verify' | 'disable'>('idle');
  const [tfaSecret, setTfaSecret] = useState('');
  const [tfaUri, setTfaUri] = useState('');
  const [tfaCode, setTfaCode] = useState('');
  const [tfaLoading, setTfaLoading] = useState(false);
  const [tfaMessage, setTfaMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Budget Guard settings (admin only)
  const [bgSettings, setBgSettings] = useState<PlatformSetting[]>([]);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgSaving, setBgSaving] = useState<string | null>(null);
  const [bgMessage, setBgMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [bgEdits, setBgEdits] = useState<Record<string, string>>({});

  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';

  const loadBudgetGuardSettings = useCallback(async () => {
    if (!isAdmin) return;
    setBgLoading(true);
    try {
      const data = await budgetGuardApi.getSettings('budget_guard');
      setBgSettings(data.settings);
      const edits: Record<string, string> = {};
      data.settings.forEach(s => { edits[s.key] = s.value; });
      setBgEdits(edits);
    } catch {
      setBgMessage({ type: 'error', text: t('settings.budgetGuardLoadFailed') });
    } finally {
      setBgLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (activeSection === 'budget_guard') {
      loadBudgetGuardSettings();
    }
  }, [activeSection, loadBudgetGuardSettings]);

  const handleSaveSetting = async (key: string) => {
    const value = bgEdits[key];
    if (value === undefined) return;
    setBgSaving(key);
    setBgMessage(null);
    try {
      await budgetGuardApi.updateSetting(key, value);
      setBgMessage({ type: 'success', text: t('settings.settingUpdated', { key }) });
      loadBudgetGuardSettings();
    } catch {
      setBgMessage({ type: 'error', text: t('settings.settingUpdateFailed', { key }) });
    } finally {
      setBgSaving(null);
    }
  };

  const authHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      const res = await fetch(`${API_BASE}/auth/profile`, {
        method: 'PATCH',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ first_name: firstName, last_name: lastName }),
      });
      const data = await res.json();
      if (res.ok) {
        updateUser({ first_name: data.first_name, last_name: data.last_name });
        setProfileMessage({ type: 'success', text: t('settings.profileUpdated') });
      } else {
        setProfileMessage({ type: 'error', text: data.detail || t('settings.profileUpdateFailed') });
      }
    } catch {
      setProfileMessage({ type: 'error', text: t('common.networkError') });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordMessage(null);

    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: t('settings.passwordTooShort') });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: t('settings.passwordsMismatch') });
      return;
    }

    setPasswordSaving(true);
    try {
      const res = await fetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setPasswordMessage({ type: 'success', text: t('settings.passwordChanged') });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordMessage({ type: 'error', text: data.detail || t('settings.passwordChangeFailed') });
      }
    } catch {
      setPasswordMessage({ type: 'error', text: t('common.networkError') });
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSetup2FA = async () => {
    setTfaLoading(true);
    setTfaMessage(null);
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/setup`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setTfaSecret(data.secret);
        setTfaUri(data.uri);
        setTfaStep('verify');
      } else {
        setTfaMessage({ type: 'error', text: data.detail || t('settings.twoFaSetupFailed') });
      }
    } catch {
      setTfaMessage({ type: 'error', text: t('common.networkShort') });
    } finally {
      setTfaLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (tfaCode.length !== 6) return;
    setTfaLoading(true);
    setTfaMessage(null);
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/verify`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ code: tfaCode }),
      });
      const data = await res.json();
      if (res.ok) {
        updateUser({ totp_enabled: true });
        setTfaStep('idle');
        setTfaCode('');
        setTfaSecret('');
        setTfaUri('');
        setTfaMessage({ type: 'success', text: '2FA enabled successfully' });
      } else {
        setTfaMessage({ type: 'error', text: data.detail || t('settings.invalidCode') });
      }
    } catch {
      setTfaMessage({ type: 'error', text: t('common.networkShort') });
    } finally {
      setTfaLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (tfaCode.length !== 6) return;
    setTfaLoading(true);
    setTfaMessage(null);
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/disable`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ code: tfaCode }),
      });
      const data = await res.json();
      if (res.ok) {
        updateUser({ totp_enabled: false });
        setTfaStep('idle');
        setTfaCode('');
        setTfaMessage({ type: 'success', text: '2FA disabled' });
      } else {
        setTfaMessage({ type: 'error', text: data.detail || t('settings.invalidCode') });
      }
    } catch {
      setTfaMessage({ type: 'error', text: t('common.networkShort') });
    } finally {
      setTfaLoading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('settings.title')}</h1>
          <p className="header-subtitle">{t('settings.subtitle')}</p>
        </div>
      </div>

      <div className="url-lists-layout">
        {/* Left Panel */}
        <aside className="url-lists-panel">
          <button
            className={`settings-nav-item ${activeSection === 'account' ? 'active' : ''}`}
            onClick={() => setActiveSection('account')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            {t('settings.tabAccount')}
          </button>
          <button
            className={`settings-nav-item ${activeSection === 'security' ? 'active' : ''}`}
            onClick={() => { setActiveSection('security'); setTfaMessage(null); setTfaStep('idle'); setTfaCode(''); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            {t('settings.tabSecurity')}
          </button>
          {isAdmin && (
            <button
              className={`settings-nav-item ${activeSection === 'budget_guard' ? 'active' : ''}`}
              onClick={() => { setActiveSection('budget_guard'); setBgMessage(null); }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
              {t('settings.tabBudgetGuard')}
            </button>
          )}
        </aside>

        {/* Editor Area */}
        <div className="url-editor-panel">
          {activeSection === 'account' && (
            <div className="settings-content">
              {/* Profile card */}
              <div className="settings-card">
                <h2 className="settings-card-title">{t('settings.profile')}</h2>
                <div className="settings-profile-header">
                  <div className="settings-avatar">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </div>
                  <div className="settings-avatar-info">
                    <span className="settings-avatar-name">{user?.first_name} {user?.last_name}</span>
                    <span className="settings-avatar-role">{user?.role}</span>
                  </div>
                </div>

                <div className="settings-form-group">
                  <label className="field-label">{t('settings.firstName')}</label>
                  <input
                    type="text"
                    className="settings-input"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                  />
                </div>
                <div className="settings-form-group">
                  <label className="field-label">{t('settings.lastName')}</label>
                  <input
                    type="text"
                    className="settings-input"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                  />
                </div>
                <div className="settings-form-group">
                  <label className="field-label">{t('settings.email')}</label>
                  <input
                    type="email"
                    className="settings-input"
                    value={user?.email || ''}
                    disabled
                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                  />
                </div>

                {profileMessage && (
                  <div className={`settings-message settings-message-${profileMessage.type}`}>
                    {profileMessage.text}
                  </div>
                )}

                <button
                  className="add-list-btn"
                  onClick={handleSaveProfile}
                  disabled={profileSaving}
                  style={{ marginTop: '8px' }}
                >
                  {profileSaving ? (
                    <span>{t('settings.saving')}</span>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>{t('common.save')}</span>
                    </>
                  )}
                </button>
              </div>

              {/* Change Password card */}
              {/* Interface language. Absent where the edition fixes it — a white-label
                  serves one market, so offering a choice there would be a lie. */}
              {!languageLocked() && (
                <div className="settings-card">
                  <h2 className="settings-card-title">{t('settings.language')}</h2>
                  <div className="settings-form-group">
                    <label className="field-label">{t('settings.interfaceLanguage')}</label>
                    <LanguageSwitcher />
                  </div>
                </div>
              )}

              <div className="settings-card">
                <h2 className="settings-card-title">{t('settings.changePassword')}</h2>

                <div className="settings-form-group">
                  <label className="field-label">{t('settings.currentPassword')}</label>
                  <input
                    type="password"
                    className="settings-input"
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    placeholder={t('settings.currentPasswordPlaceholder')}
                  />
                </div>
                <div className="settings-form-group">
                  <label className="field-label">{t('settings.newPassword')}</label>
                  <input
                    type="password"
                    className="settings-input"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder={t('settings.newPasswordPlaceholder')}
                  />
                </div>
                <div className="settings-form-group">
                  <label className="field-label">{t('settings.confirmPassword')}</label>
                  <input
                    type="password"
                    className="settings-input"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder={t('settings.confirmPasswordPlaceholder')}
                  />
                </div>

                {passwordMessage && (
                  <div className={`settings-message settings-message-${passwordMessage.type}`}>
                    {passwordMessage.text}
                  </div>
                )}

                <button
                  className="add-list-btn"
                  onClick={handleChangePassword}
                  disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}
                  style={{ marginTop: '8px' }}
                >
                  {passwordSaving ? (
                    <span>{t('settings.changing')}</span>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                      <span>{t('settings.changePassword')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {activeSection === 'security' && (
            <div className="settings-content">
              <div className="settings-card">
                <h2 className="settings-card-title">{t('settings.twoFactor')}</h2>

                <div className="tfa-status-row">
                  <div className="tfa-status-info">
                    <span className={`tfa-status-badge ${user?.totp_enabled ? 'tfa-enabled' : 'tfa-disabled'}`}>
                      {user?.totp_enabled ? t('settings.enabled') : t('settings.disabled')}
                    </span>
                    <p className="tfa-description">
                      {user?.totp_enabled
                        ? t('settings.twoFaProtected')
                        : t('settings.twoFaOffer')
                      }
                    </p>
                  </div>
                </div>

                {tfaMessage && (
                  <div className={`settings-message settings-message-${tfaMessage.type}`}>
                    {tfaMessage.text}
                  </div>
                )}

                {/* Idle state — show enable/disable button */}
                {tfaStep === 'idle' && !user?.totp_enabled && (
                  <button
                    className="add-list-btn"
                    onClick={handleSetup2FA}
                    disabled={tfaLoading}
                  >
                    {tfaLoading ? <span>{t('settings.settingUp')}</span> : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        </svg>
                        <span>{t('settings.enable2fa')}</span>
                      </>
                    )}
                  </button>
                )}

                {tfaStep === 'idle' && user?.totp_enabled && (
                  <button
                    className="add-list-btn tfa-disable-btn"
                    onClick={() => { setTfaStep('disable'); setTfaCode(''); setTfaMessage(null); }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="15" y1="9" x2="9" y2="15"/>
                      <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    <span>{t('settings.disable2fa')}</span>
                  </button>
                )}

                {/* Setup step — show QR code and secret */}
                {tfaStep === 'verify' && (
                  <div className="tfa-setup-block">
                    <p className="tfa-instruction">{t('settings.scanQr')}</p>
                    <div className="tfa-qr-wrapper">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(tfaUri)}`}
                        alt="2FA QR Code"
                        className="tfa-qr-image"
                      />
                    </div>
                    <div className="tfa-manual-key">
                      <span className="tfa-manual-label">{t('settings.enterKeyManually')}</span>
                      <code className="tfa-secret-code">{tfaSecret}</code>
                    </div>
                    <div className="settings-form-group" style={{ marginTop: 20 }}>
                      <label className="field-label">{t('settings.codeFromApp')}</label>
                      <input
                        type="text"
                        className="settings-input tfa-code-input"
                        value={tfaCode}
                        onChange={e => setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        autoFocus
                      />
                    </div>
                    <div className="tfa-actions">
                      <button
                        className="add-list-btn"
                        onClick={handleVerify2FA}
                        disabled={tfaLoading || tfaCode.length !== 6}
                      >
                        {tfaLoading ? <span>{t('settings.verifying')}</span> : <span>{t('settings.verifyEnable')}</span>}
                      </button>
                      <button
                        className="add-list-btn tfa-cancel-btn"
                        onClick={() => { setTfaStep('idle'); setTfaCode(''); setTfaMessage(null); }}
                      >{t('common.cancel')}</button>
                    </div>
                  </div>
                )}

                {/* Disable step — ask for current code */}
                {tfaStep === 'disable' && (
                  <div className="tfa-setup-block">
                    <p className="tfa-instruction">{t('settings.codeToDisable')}</p>
                    <div className="settings-form-group">
                      <label className="field-label">6-digit code</label>
                      <input
                        type="text"
                        className="settings-input tfa-code-input"
                        value={tfaCode}
                        onChange={e => setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        autoFocus
                      />
                    </div>
                    <div className="tfa-actions">
                      <button
                        className="add-list-btn tfa-disable-btn"
                        onClick={handleDisable2FA}
                        disabled={tfaLoading || tfaCode.length !== 6}
                      >
                        {tfaLoading ? <span>{t('settings.disabling')}</span> : <span>{t('settings.disable2fa')}</span>}
                      </button>
                      <button
                        className="add-list-btn tfa-cancel-btn"
                        onClick={() => { setTfaStep('idle'); setTfaCode(''); setTfaMessage(null); }}
                      >{t('common.cancel')}</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'budget_guard' && isAdmin && (
            <div className="settings-content">
              <div className="settings-card">
                <h2 className="settings-card-title">{t('settings.budgetGuard')}</h2>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
                  {t('settings.budgetGuardHint')}
                </p>

                {bgMessage && (
                  <div className={`settings-message settings-message-${bgMessage.type}`}>
                    {bgMessage.text}
                  </div>
                )}

                {bgLoading ? (
                  <div style={{ padding: 20, color: '#888' }}>{t('settings.loading')}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {bgSettings.map(setting => (
                      <div key={setting.key} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 12px', background: '#f8f9fa', borderRadius: 6,
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>
                            {setting.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </div>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                            {setting.description}
                          </div>
                        </div>
                        <input
                          type={setting.value_type === 'int' ? 'number' : 'text'}
                          className="settings-input"
                          style={{ width: 100, textAlign: 'right' }}
                          value={bgEdits[setting.key] ?? setting.value}
                          onChange={e => setBgEdits({ ...bgEdits, [setting.key]: e.target.value })}
                          step={setting.value_type === 'float' ? '0.01' : '1'}
                        />
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleSaveSetting(setting.key)}
                          disabled={bgSaving === setting.key || bgEdits[setting.key] === setting.value}
                          style={{ padding: '4px 12px', fontSize: 11 }}
                        >
                          {bgSaving === setting.key ? '...' : 'Save'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="settings-card" style={{ marginTop: 16 }}>
                <h2 className="settings-card-title">{t('settings.monitoring')}</h2>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    className="add-list-btn"
                    onClick={async () => {
                      try {
                        const result = await budgetGuardApi.reconcile();
                        setBgMessage({ type: 'success', text: `Reconciliation complete: ${result.corrected || 0} corrected` });
                      } catch {
                        setBgMessage({ type: 'error', text: t('settings.reconciliationFailed') });
                      }
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="23 4 23 10 17 10"/>
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                    </svg>
                    <span>{t('settings.runReconciliation')}</span>
                  </button>
                  <button
                    className="add-list-btn"
                    onClick={async () => {
                      try {
                        const result = await budgetGuardApi.initRedis();
                        setBgMessage({ type: 'success', text: `Redis initialized: ${result.accounts_initialized} accounts` });
                      } catch {
                        setBgMessage({ type: 'error', text: t('settings.redisInitFailed') });
                      }
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                    </svg>
                    <span>{t('settings.reinitRedis')}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
